import { Button, Card, CardBody, CardHeader, Spinner } from '@heroui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { orderService } from '../services/orderService';
import { GuestOrderTracking } from '../types/orders';

const GuestConfirmation: React.FC = () => {
  const pageTitle = 'Confirming Payment';
  const pageDescription =
    'We are confirming your payment and preparing your order.';
  const location = useLocation();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<GuestOrderTracking | null>(null);
  const [pollingStatus, setPollingStatus] = useState<
    'polling' | 'complete' | 'timeout'
  >('polling');
  const timeoutRef = useRef<number | null>(null);

  const orderNumber = location.state?.orderNumber as string | undefined;
  const email = location.state?.email as string | undefined;

  useEffect(() => {
    if (!orderNumber || !email) {
      navigate('/guest-order-tracking', { replace: true });
      return;
    }

    const startTime = Date.now();
    let isCancelled = false;

    const poll = async () => {
      if (isCancelled) return;

      try {
        const order = await orderService.trackGuestOrder(orderNumber, email);
        if (isCancelled) return;
        setOrderData(order);

        const paymentStatus = order.paymentStatus.toLowerCase();
        if (
          paymentStatus === 'paid' ||
          paymentStatus === 'failed' ||
          paymentStatus === 'cancelled'
        ) {
          setPollingStatus('complete');
          return;
        }
      } catch (error) {}

      const elapsedMs = Date.now() - startTime;
      if (elapsedMs >= 120000) {
        setPollingStatus('timeout');
        return;
      }

      const delay = elapsedMs < 30000 ? 2000 : 5000;
      timeoutRef.current = window.setTimeout(poll, delay);
    };

    poll();

    return () => {
      isCancelled = true;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [email, navigate, orderNumber]);

  const handleTrackOrder = () => {
    navigate('/guest-order-tracking', {
      state: { email, orderNumber },
    });
  };

  const paymentStatus = orderData?.paymentStatus?.toLowerCase();
  const isPaid = paymentStatus === 'paid';
  const isFailed = paymentStatus === 'failed' || paymentStatus === 'cancelled';

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <Card>
          <CardHeader>
            <h1 className='text-2xl font-semibold'>Confirming your payment</h1>
          </CardHeader>
          <CardBody className='space-y-4'>
            {pollingStatus === 'polling' && (
              <div className='flex items-center space-x-3 text-gray-600'>
                <Spinner size='sm' />
                <p>We are confirming your payment. This may take a moment.</p>
              </div>
            )}

            {pollingStatus === 'complete' && isPaid && (
              <div className='text-green-600'>
                Payment confirmed. Your order is being prepared.
              </div>
            )}

            {pollingStatus === 'complete' && isFailed && (
              <div className='text-red-600'>
                Payment failed or was cancelled. Please review your order
                details.
              </div>
            )}

            {pollingStatus === 'timeout' && (
              <div className='text-yellow-600'>
                We are still confirming your payment. Please check your order
                status.
              </div>
            )}

            <div className='text-sm text-gray-500'>
              Order reference: {orderNumber || 'Pending'}
            </div>

            <div className='flex justify-end'>
              <Button color='primary' onClick={handleTrackOrder}>
                View order status
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default GuestConfirmation;
